// IntegrationManager.tsx - External integrations management
import React, { useState } from 'react';
import {
  Card,
  Text,
  Button,
  Stack,
  Group,
  Badge,
  Modal,
  TextInput,
  Select,
  Switch,
  NumberInput,
  MultiSelect,
  Alert,
  ActionIcon,
  Tooltip,
  Progress,
  Menu,
  Table,
  Tabs,
  Divider,
} from '@mantine/core';
import {
  IconPlus,
  IconRefresh,
  IconSettings,
  IconTrash,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconDots,
  IconExternalLink,
  IconHistory,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';

interface Integration {
  id: string;
  source: string;
  status: 'active' | 'inactive' | 'error' | 'pending';
  config: {
    syncInterval?: number;
    autoImport?: boolean;
    autoScore?: boolean;
    filters?: {
      brands?: string[];
      models?: string[];
      minPrice?: number;
      maxPrice?: number;
      regions?: string[];
    };
  };
  lastSyncAt?: string;
  lastSyncStatus?: 'success' | 'error';
  lastSyncError?: string;
  syncEnabled: boolean;
  createdAt: string;
}

export function IntegrationManager() {
  const [opened, setOpened] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const queryClient = useQueryClient();

  // Fetch integrations
  const { data: integrationsData, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await fetch('/api/integrations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
  });

  const integrations: Integration[] = integrationsData?.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/integrations/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      notifications.show({
        title: 'Integration Deleted',
        message: 'Integration removed successfully',
        color: 'green',
      });
    },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/integrations/${id}/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      notifications.show({
        title: 'Sync Complete',
        message: data.message || 'Integration synced successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Sync Failed',
        message: 'Failed to sync integration',
        color: 'red',
      });
    },
  });

  // Test connection mutation
  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/integrations/${id}/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.data.success) {
        notifications.show({
          title: 'Connection Successful',
          message: data.data.message,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Connection Failed',
          message: data.data.message,
          color: 'red',
        });
      }
    },
  });

  const handleDelete = (integration: Integration) => {
    modals.openConfirmModal({
      title: 'Delete Integration',
      children: (
        <Text size="sm">
          Are you sure you want to delete the {integration.source} integration? This action cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(integration.id),
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'error':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getSourceName = (source: string) => {
    const names: Record<string, string> = {
      autoscout24: 'AutoScout24',
      mobile_de: 'Mobile.de',
      ebay_motors: 'eBay Motors',
      facebook: 'Facebook Marketplace',
    };
    return names[source] || source;
  };

  const getSourceLogo = (source: string) => {
    // You can replace these with actual logos
    return `https://logo.clearbit.com/${source}.com`;
  };

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        {/* Header */}
        <Group justify="space-between" mb="md">
          <div>
            <Text size="lg" fw={700}>
              External Integrations
            </Text>
            <Text size="sm" c="dimmed">
              Connect to external platforms for automatic lead generation
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setSelectedIntegration(null);
              setOpened(true);
            }}
          >
            Add Integration
          </Button>
        </Group>

        {/* Integrations Grid */}
        {isLoading ? (
          <Text c="dimmed" ta="center" py="xl">
            Loading integrations...
          </Text>
        ) : integrations.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            <Text size="sm">
              No integrations configured yet. Click "Add Integration" to get started.
            </Text>
          </Alert>
        ) : (
          <Stack gap="md">
            {integrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onEdit={() => {
                  setSelectedIntegration(integration);
                  setOpened(true);
                }}
                onDelete={() => handleDelete(integration)}
                onSync={() => syncMutation.mutate(integration.id)}
                onTest={() => testMutation.mutate(integration.id)}
                isSyncing={syncMutation.isPending}
                isTesting={testMutation.isPending}
              />
            ))}
          </Stack>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <IntegrationModal
        opened={opened}
        onClose={() => {
          setOpened(false);
          setSelectedIntegration(null);
        }}
        integration={selectedIntegration}
      />
    </>
  );
}

interface IntegrationCardProps {
  integration: Integration;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
  onTest: () => void;
  isSyncing: boolean;
  isTesting: boolean;
}

function IntegrationCard({
  integration,
  onEdit,
  onDelete,
  onSync,
  onTest,
  isSyncing,
  isTesting,
}: IntegrationCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'error':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getSourceName = (source: string) => {
    const names: Record<string, string> = {
      autoscout24: 'AutoScout24',
      mobile_de: 'Mobile.de',
      ebay_motors: 'eBay Motors',
      facebook: 'Facebook Marketplace',
    };
    return names[source] || source;
  };

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" wrap="nowrap">
        <Group>
          {/* Logo placeholder */}
          <Card withBorder w={60} h={60} bg="gray.1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text size="xs" fw={700}>
              {getSourceName(integration.source).substring(0, 2)}
            </Text>
          </Card>

          <Stack gap="xs">
            <Group gap="xs">
              <Text size="lg" fw={600}>
                {getSourceName(integration.source)}
              </Text>
              <Badge color={getStatusColor(integration.status)} variant="filled">
                {integration.status}
              </Badge>
              {integration.syncEnabled && (
                <Badge color="blue" variant="light">
                  Auto-sync enabled
                </Badge>
              )}
            </Group>

            <Group gap="md">
              {integration.lastSyncAt && (
                <Text size="xs" c="dimmed">
                  Last sync:{' '}
                  {new Date(integration.lastSyncAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}
              {integration.config.syncInterval && (
                <Text size="xs" c="dimmed">
                  Sync interval: {integration.config.syncInterval} min
                </Text>
              )}
            </Group>

            {integration.lastSyncError && (
              <Alert icon={<IconX size={14} />} color="red" p="xs">
                <Text size="xs">{integration.lastSyncError}</Text>
              </Alert>
            )}
          </Stack>
        </Group>

        <Group gap="xs">
          <Tooltip label="Test connection">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={onTest}
              loading={isTesting}
            >
              <IconCheck size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Sync now">
            <ActionIcon
              variant="light"
              color="green"
              onClick={onSync}
              loading={isSyncing}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="light">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconSettings size={16} />} onClick={onEdit}>
                Settings
              </Menu.Item>
              <Menu.Item
                leftSection={<IconHistory size={16} />}
                component="a"
                href={`/integrations/${integration.id}/logs`}
              >
                View Logs
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={onDelete}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Card>
  );
}

interface IntegrationModalProps {
  opened: boolean;
  onClose: () => void;
  integration?: Integration | null;
}

function IntegrationModal({ opened, onClose, integration }: IntegrationModalProps) {
  const [source, setSource] = useState(integration?.source || '');
  const [config, setConfig] = useState(integration?.config || {});
  const [credentials, setCredentials] = useState<any>({});
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const url = integration
        ? `/api/integrations/${integration.id}`
        : '/api/integrations';
      const method = integration ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      notifications.show({
        title: 'Success',
        message: integration ? 'Integration updated' : 'Integration created',
        color: 'green',
      });
      onClose();
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to save integration',
        color: 'red',
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      source,
      config,
      credentials,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={integration ? 'Edit Integration' : 'Add Integration'}
      size="lg"
    >
      <Stack gap="md">
        <Select
          label="Platform"
          placeholder="Select integration platform"
          data={[
            { value: 'autoscout24', label: 'AutoScout24' },
            { value: 'mobile_de', label: 'Mobile.de' },
            { value: 'ebay_motors', label: 'eBay Motors' },
            { value: 'facebook', label: 'Facebook Marketplace' },
          ]}
          value={source}
          onChange={(val) => setSource(val || '')}
          disabled={!!integration}
        />

        <TextInput
          label="API Key / Access Token"
          placeholder="Enter your API credentials"
          value={credentials.accessToken || credentials.apiKey || ''}
          onChange={(e) =>
            setCredentials({
              ...credentials,
              [source === 'mobile_de' ? 'apiKey' : 'accessToken']: e.currentTarget.value,
            })
          }
        />

        <Divider label="Configuration" />

        <Switch
          label="Enable auto-sync"
          description="Automatically sync leads at regular intervals"
          checked={config.autoImport || false}
          onChange={(e) =>
            setConfig({ ...config, autoImport: e.currentTarget.checked })
          }
        />

        <Switch
          label="Enable auto-scoring"
          description="Automatically score new leads"
          checked={config.autoScore || false}
          onChange={(e) =>
            setConfig({ ...config, autoScore: e.currentTarget.checked })
          }
        />

        <NumberInput
          label="Sync Interval (minutes)"
          placeholder="60"
          min={15}
          max={1440}
          value={config.syncInterval || 60}
          onChange={(val) =>
            setConfig({ ...config, syncInterval: Number(val) })
          }
        />

        <Divider label="Filters (Optional)" />

        <MultiSelect
          label="Brands"
          placeholder="Filter by brands"
          data={['BMW', 'Mercedes', 'Audi', 'Volkswagen', 'Porsche']}
          value={config.filters?.brands || []}
          onChange={(val) =>
            setConfig({
              ...config,
              filters: { ...config.filters, brands: val },
            })
          }
        />

        <Group grow>
          <NumberInput
            label="Min Price (€)"
            placeholder="0"
            value={config.filters?.minPrice || undefined}
            onChange={(val) =>
              setConfig({
                ...config,
                filters: { ...config.filters, minPrice: Number(val) },
              })
            }
          />
          <NumberInput
            label="Max Price (€)"
            placeholder="100000"
            value={config.filters?.maxPrice || undefined}
            onChange={(val) =>
              setConfig({
                ...config,
                filters: { ...config.filters, maxPrice: Number(val) },
              })
            }
          />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saveMutation.isPending}>
            {integration ? 'Save Changes' : 'Add Integration'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
